<script setup lang="ts">
/**
 * What still needs bringing, and what you said you would bring
 * (SPEC §6.19, §6.20, §6.21, §8.7).
 *
 * Three states an item can be in, and the screen has to make them tellable
 * apart at a glance: the host is providing it, somebody has it covered, or it
 * is still needed. Anything else is a family reading the menu carefully and
 * still bringing a fourth salad.
 */
import { computed, ref } from 'vue'
import { ApiError, endpoints, type FamilyMenuItemResponse, type FamilyMealResponse } from '@/api'
import { asCount, asNumber } from '@/utils/numbers'

const props = defineProps<{
  menu: FamilyMealResponse['menu']
  /** False once the meal is no longer something to sign up to. */
  open: boolean
  /**
   * The home screen's two cards only: what you are bringing and what is still
   * needed (SPEC §6.11). The whole menu is a page somebody chooses to open,
   * not something to scroll past on the way to the RSVP buttons.
   */
  compact?: boolean
}>()

const emit = defineEmits<{ changed: [] }>()

const busy = ref<string | null>(null)
const error = ref<string | null>(null)
const announcement = ref('')

const claiming = ref<FamilyMenuItemResponse | null>(null)
const quantity = ref(1)
const description = ref('')

/** Everything this household has promised, across the whole menu. */
const yours = computed(() =>
  props.menu
    .flatMap((section) => section.items)
    .filter((item) => item.yourClaim)
    .map((item) => ({ item, claim: item.yourClaim! })),
)

const stillNeeded = computed(() =>
  props.menu
    .flatMap((section) => section.items)
    .filter(
      (item) =>
        item.participationMode === 'SignupRequired' && (asNumber(item.remainingQuantity) ?? 1) > 0,
    ),
)

function remaining(item: FamilyMenuItemResponse): number | null {
  return asNumber(item.remainingQuantity)
}

function unit(item: FamilyMenuItemResponse): string {
  return item.unitLabel ? ` ${item.unitLabel}` : ''
}

/**
 * How the item reads when nobody is being asked for it.
 *
 * Distinguishing these matters more than it looks. An item that says nothing
 * about who is bringing it reads as unclaimed.
 */
function state(item: FamilyMenuItemResponse): 'host' | 'info' | 'covered' | 'needed' {
  if (item.participationMode === 'HostProvided') return 'host'
  if (item.participationMode === 'Informational') return 'info'

  return (remaining(item) ?? 1) > 0 ? 'needed' : 'covered'
}

function describe(caught: unknown): string {
  // A 409 here is not a failure, it is news: somebody else got there first.
  if (caught instanceof ApiError && caught.status === 409) {
    return 'Another family just took the last one. Have a look at what is left.'
  }

  return (caught instanceof ApiError && caught.problem?.title) || 'That did not work.'
}

function open_(item: FamilyMenuItemResponse) {
  claiming.value = item
  quantity.value = Math.max(1, Math.min(remaining(item) ?? 1, 1))
  description.value = item.yourClaim?.description ?? ''
}

async function claim() {
  const item = claiming.value

  if (!item) return

  claiming.value = null
  busy.value = item.id
  error.value = null

  try {
    if (item.yourClaim) {
      await endpoints.amendContribution(item.yourClaim.id, {
        eventMenuItemId: item.id,
        quantity: quantity.value,
        description: description.value || null,
      })
    } else {
      await endpoints.claimContribution({
        eventMenuItemId: item.id,
        quantity: quantity.value,
        description: description.value || null,
      })
    }

    announcement.value = `You are bringing ${item.name}.`
    emit('changed')
  } catch (caught) {
    error.value = describe(caught)

    // Whatever happened, the list on screen is now out of date. Refreshing is
    // the useful half of saying somebody else got there first.
    emit('changed')
  } finally {
    busy.value = null
  }
}

async function release(item: FamilyMenuItemResponse) {
  if (!item.yourClaim) return

  busy.value = item.id
  error.value = null

  try {
    await endpoints.releaseContribution(item.yourClaim.id)
    announcement.value = `You are no longer bringing ${item.name}.`
    emit('changed')
  } catch (caught) {
    error.value = describe(caught)
    emit('changed')
  } finally {
    busy.value = null
  }
}
</script>

<template>
  <section class="menu">
    <p aria-live="polite" class="menu__announcement">{{ announcement }}</p>

    <v-alert v-if="error" type="info" variant="tonal" class="mb-4">{{ error }}</v-alert>

    <!--
      On the home screen this card is always drawn, saying "Nothing yet" when
      there is nothing (SPEC §6.11). A card that disappears when empty reads as
      a family having nothing to do, which is the opposite of what it means.
    -->
    <template v-if="compact || yours.length > 0">
      <h2 class="menu__heading">You are bringing</h2>
      <p v-if="yours.length === 0" class="menu__empty">Nothing yet.</p>
      <ul class="menu__list">
        <li v-for="{ item, claim: mine } in yours" :key="item.id" class="menu__row menu__row--mine">
          <div>
            <p class="menu__name">
              {{ mine.description || item.name }}
              <span v-if="mine.description" class="menu__for">for {{ item.name }}</span>
            </p>
            <p class="menu__meta">{{ mine.quantity }}{{ unit(item) }}</p>
          </div>

          <div v-if="open" class="menu__actions">
            <v-btn
              :disabled="busy !== null"
              class="menu__action"
              variant="text"
              density="comfortable"
              @click="open_(item)"
            >
              Change
            </v-btn>
            <v-btn
              :loading="busy === item.id"
              :disabled="busy !== null"
              class="menu__action"
              variant="text"
              density="comfortable"
              @click="release(item)"
            >
              Never mind
            </v-btn>
          </div>
        </li>
      </ul>
    </template>

    <template v-if="stillNeeded.length > 0">
      <h2 class="menu__heading">Still needed</h2>
      <ul class="menu__list">
        <li v-for="item in stillNeeded" :key="item.id" class="menu__row">
          <div>
            <p class="menu__name">{{ item.name }}</p>
            <p class="menu__meta">
              {{ remaining(item) }}{{ unit(item) }} still needed
              <template v-if="item.notes"> · {{ item.notes }}</template>
            </p>

            <!-- Three desserts, with the brownies and the pie already named. -->
            <ul v-if="item.isFlexible && item.claims.length > 0" class="menu__claims">
              <li v-for="c in item.claims" :key="c.id">
                {{ c.description || 'Something' }}
                <template v-if="c.householdName"> — {{ c.householdName }}</template>
                <template v-else-if="c.isYours"> — you</template>
              </li>
            </ul>
          </div>

          <v-btn
            v-if="open"
            :loading="busy === item.id"
            :disabled="busy !== null"
            class="menu__action"
            color="primary"
            variant="outlined"
            @click="open_(item)"
          >
            I'll bring this
          </v-btn>
        </li>
      </ul>
    </template>

    <template v-if="!compact">
      <h2 class="menu__heading">On the menu</h2>
      <div v-for="section in menu" :key="section.id" class="menu__section">
        <h3 class="menu__section-name">{{ section.name }}</h3>
        <ul class="menu__list">
          <li v-for="item in section.items" :key="item.id" class="menu__row">
            <div>
              <p class="menu__name">{{ item.name }}</p>
              <p class="menu__meta" :class="`menu__meta--${state(item)}`">
                <template v-if="state(item) === 'host'">Provided by the host</template>
                <template v-else-if="state(item) === 'info'">Just so you know</template>
                <template v-else-if="state(item) === 'covered'">
                  Covered — {{ asCount(item.claimedQuantity) }}{{ unit(item) }} promised
                </template>
                <template v-else> {{ remaining(item) }}{{ unit(item) }} still needed </template>
              </p>
            </div>
          </li>
        </ul>
      </div>
    </template>

    <v-dialog
      :model-value="claiming !== null"
      max-width="28rem"
      @update:model-value="claiming = null"
    >
      <v-card v-if="claiming">
        <v-card-title tag="h2">{{ claiming.name }}</v-card-title>
        <v-card-text class="menu__form">
          <v-text-field
            v-if="claiming.isFlexible"
            v-model="description"
            label="What are you bringing"
            placeholder="Brownies"
            variant="outlined"
            density="comfortable"
            hide-details
          />
          <v-text-field
            v-model.number="quantity"
            type="number"
            min="1"
            label="How many"
            variant="outlined"
            density="comfortable"
            hide-details
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="claiming = null">Cancel</v-btn>
          <v-btn :disabled="quantity < 1" variant="flat" color="primary" @click="claim">
            {{ claiming.yourClaim ? 'Save' : "I'll bring it" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<style scoped>
.menu {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.menu__announcement {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.menu__heading {
  font-size: 1.125rem;
  margin: 1rem 0 0.25rem;
}

.menu__section-name {
  font-size: 0.8125rem;
  letter-spacing: 0.04em;
  color: rgb(var(--v-theme-on-surface-variant));
  margin: 0.75rem 0 0.25rem;
}

.menu__list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.menu__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem;
  border: 1px solid rgb(var(--v-border-color), var(--v-border-opacity));
  border-radius: 0.5rem;
}

.menu__row--mine {
  border-color: rgb(var(--v-theme-primary));
}

.menu__name {
  margin: 0;
  font-weight: 600;
}

.menu__for {
  font-weight: 400;
  color: rgb(var(--v-theme-on-surface-variant));
}

.menu__meta {
  margin: 0.1rem 0 0;
  font-size: 0.875rem;
  color: rgb(var(--v-theme-on-surface-variant));
}

.menu__empty {
  margin: 0;
  color: rgb(var(--v-theme-on-surface-variant));
}

.menu__meta--needed {
  color: rgb(var(--v-theme-primary));
}

.menu__claims {
  margin: 0.4rem 0 0;
  padding-left: 1.1rem;
  font-size: 0.875rem;
  color: rgb(var(--v-theme-on-surface-variant));
}

.menu__actions {
  display: flex;
  flex-wrap: wrap;
}

/* Thumb-sized, like everything else on the family surface (SPEC §9.5). */
.menu__action {
  min-height: 2.75rem;
}

.menu__form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
</style>
